import React, { useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import { Checkbox } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import { useTranslation } from 'react-i18next';
import classes from '../scss/addEditFormModal.module.scss';
import TextField from '../../Form/TextField/TextField';
import CustomCalendarHeader from '../../Form/CalendarInput/CustomCalendarHeader';
import CalendarInput from '../../Form/CalendarInput/CalendarInput';
import { getLastDayOFMounth, isValidDate } from '../../../../utils/helpers';
import { useWindowSize } from '../../../../utils/hooks';
import { deleteModalConfigs, maxMobileSize, maxSymbolQTY } from '../../../../utils/constants';
import ValidationErrorLabel from '../../ValidationErrorLabel/ValidationErrorLabel';
import { BinOutlinedIcon } from '../../Icons';
import DeleteDialog from '../DeleteDialog';

import('react-datepicker/dist/react-datepicker.css');
import('../../../../scss/datepicker.css');

function EducationsForm(props) {
  const { addEditForm, withCurrentlyCheckBox, deleteHandler } = props;

  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const deleteModalConfig = useMemo(() => deleteModalConfigs(t), [t]);
  const size = useWindowSize();
  const useStartDate = isValidDate(addEditForm.values.startdate) ? addEditForm.values.startdate : '';
  const useEndDate = isValidDate(addEditForm.values.enddate) ? addEditForm.values.enddate : '';

  const changeCurrentlyCheckbox = (e) => {
    addEditForm.setFieldValue('enddate', new Date());
    addEditForm.setFieldValue('currentlyStudyHere', e.target.checked);
  };

  return (
    <div className={classes.formContainer}>
      <div className={`col-md-3 ${classes.textField}`}>
        <TextField
          id="school"
          name="school"
          placeholder="School"
          value={addEditForm.values.school}
          onChange={addEditForm.handleChange}
          error={addEditForm.touched.school && Boolean(addEditForm.errors.school)}
          errorText={addEditForm.touched.school && addEditForm.errors.school}
          clearable
          counter={{ qty: addEditForm.values.school?.length, limit: maxSymbolQTY.SCHOOL }}
        />
        {addEditForm.values.school && (
          <div
            data-testid="clearBtn"
            role="presentation"
            className={classes.closeIcon}
            onClick={() => addEditForm.setFieldValue('school', '', true)}
          >
            <CloseIcon fontSize="8px" />
          </div>
        )}
      </div>

      <div className={`col-md-3 ${classes.textField}`}>
        <TextField
          id="degree"
          name="degree"
          placeholder="Degree"
          value={addEditForm.values.degree}
          onChange={addEditForm.handleChange}
          error={addEditForm.touched.degree && Boolean(addEditForm.errors.degree)}
          errorText={addEditForm.touched.degree && addEditForm.errors.degree}
          clearable
          counter={{ qty: addEditForm.values.degree?.length, limit: maxSymbolQTY.DEGREE }}
        />
        {addEditForm.values.degree && (
          <div
            data-testid="clearBtn"
            role="presentation"
            className={classes.closeIcon}
            onClick={() => addEditForm.setFieldValue('degree', '', true)}
          >
            <CloseIcon fontSize="8px" />
          </div>
        )}
      </div>
      <div className={`col-md-3 ${classes.textField}`}>
        <TextField
          id="fieldofstudy"
          name="fieldofstudy"
          placeholder="Field of Study"
          value={addEditForm.values.fieldofstudy}
          onChange={addEditForm.handleChange}
          error={addEditForm.touched.fieldofstudy && Boolean(addEditForm.errors.fieldofstudy)}
          errorText={addEditForm.touched.fieldofstudy && addEditForm.errors.fieldofstudy}
          clearable
          counter={{ qty: addEditForm.values.fieldofstudy?.length, limit: maxSymbolQTY.FIELD_OF_STUDY }}
        />
        {addEditForm.values.fieldofstudy && (
          <div
            data-testid="clearBtn"
            role="presentation"
            className={classes.closeIcon}
            onClick={() => addEditForm.setFieldValue('fieldofstudy', '', true)}
          >
            <CloseIcon fontSize="8px" />
          </div>
        )}
      </div>

      <div className="col-md-4 d-flex flex-row justify-content-start align-items-end gap-2">
        <DatePicker
          withPortal={size.width < maxMobileSize}
          portalId="root-portal"
          selected={useStartDate}
          name="startdate"
          data-testid="startDate"
          dateFormat="MMM yyyy"
          renderCustomHeader={
            // eslint-disable-next-line react/jsx-props-no-spreading,no-shadow
            (props) => <CustomCalendarHeader {...props} withMonth={false} />
          }
          onChange={(e) => addEditForm.setFieldValue('startdate', e, true)}
          showMonthYearPicker
          customInput={
            <CalendarInput
              label="Start Date"
              touched={addEditForm.touched.startdate}
              errors={addEditForm.errors.startdate}
            />
          }
        />
        <DatePicker
          disabled={withCurrentlyCheckBox && addEditForm.values.currentlyStudyHere}
          withPortal={size.width < maxMobileSize}
          portalId="root-portal"
          selected={useEndDate}
          name="enddate"
          data-testid="endDate"
          dateFormat="MMM yyyy"
          renderCustomHeader={
            // eslint-disable-next-line react/jsx-props-no-spreading,no-shadow
            (props) => <CustomCalendarHeader {...props} withMonth={false} />
          }
          onChange={(e) => addEditForm.setFieldValue('enddate', getLastDayOFMounth(e), true)}
          showMonthYearPicker
          customInput={
            <CalendarInput
              disabled={withCurrentlyCheckBox && addEditForm.values.currentlyStudyHere}
              label="End Date"
              touched={addEditForm.touched.enddate}
              errors={addEditForm.errors.enddate}
            />
          }
        />
        {withCurrentlyCheckBox && (
          <div className={classes.currentCheckBox}>
            <Checkbox
              icon={<div className={classes.customCheck} />}
              checkedIcon={
                <div className={classes.customCheck}>
                  <DoneIcon />
                </div>
              }
              onClick={changeCurrentlyCheckbox}
              checked={addEditForm.values.currentlyStudyHere}
              sx={{ '& .MuiSvgIcon-root': { fontSize: 15 }, padding: 0 }}
            />
            <span className={classes.checkBoxLabel}>I Currently Study Here</span>
          </div>
        )}
      </div>
      {addEditForm.touched.currentlyStudyHere && addEditForm.errors.currentlyStudyHere && (
        <ValidationErrorLabel message={addEditForm.errors.currentlyStudyHere} />
      )}
      {!!deleteHandler && (
        <div className={classes.deleteBtn} role="presentation" onClick={() => setOpen(true)}>
          <span>{t('common.general.delete')}</span> <BinOutlinedIcon />
        </div>
      )}
      <DeleteDialog
        open={open}
        closeHandler={() => setOpen(false)}
        confirmHandler={deleteHandler}
        config={deleteModalConfig.educations}
      />
    </div>
  );
}

export default EducationsForm;
